const { formatPhoneInputForRow, formatPhoneValueForRow, isPhoneColumnLabel } = require('./phoneNormalization');

function normalizeLabel(value) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

const DOMAIN_SHEETS = new Set([
  'usuarios', 'usuarios para revisar', 'usuarios eliminados', 'roles',
  'iglesia - estado', 'iglesia - ciudad', 'iglesia - nombre', 'pais', 'idioma',
  'solicitudes - nuevo usuario', 'solicitudes - cambio de rol', 'solicitudes - eliminar usuario',
  'solicitudes - mover usuario', 'solicitudes - datos personales', 'estado de sedes',
  'correos_notificaciones_usuarios', 'correo notificaciones',
]);

function isCredentialSheet(sheetName) {
  return /(?:^|[\s_-])(?:auth|passwords?|contrasenas?|credenciales|autenticacion)(?:$|[\s_-])/.test(normalizeLabel(sheetName));
}

function isNormalizableSheet(sheetName) {
  return DOMAIN_SHEETS.has(normalizeLabel(sheetName)) || isCredentialSheet(sheetName);
}

const PERSON_PREFIX = '(?:(?:solicitado por|solicitante|usuario|confirmado por|actualizado por|responsable)\\s*-\\s*)?';
const VALUE_SUFFIX = '(?:\\s+(?:actual|solicitad[oa]|nuev[oa]|current|requested|new))?';
const EMAIL_HEADER = new RegExp(`^${PERSON_PREFIX}(?:email|correo|correo electronico|e-mail)${VALUE_SUFFIX}$`);
const NAME_HEADER = new RegExp(`^${PERSON_PREFIX}(?:nombre|nombres|apellido|apellidos|nombre completo)${VALUE_SUFFIX}$`);
const DESCRIPTION_HEADER = new RegExp(`^${PERSON_PREFIX}(?:(?:iglesia\\s*-\\s*(?:estado|ciudad|nombre))|pais|idioma|lenguaje|labor\\s*/\\s*rol|labor|rol)${VALUE_SUFFIX}$`);

function getValueKind(header, sheetName) {
  const label = normalizeLabel(header);
  if (EMAIL_HEADER.test(label)) return 'email';
  if (NAME_HEADER.test(label)) return normalizeLabel(sheetName) === 'roles' ? 'description' : 'name';
  if (isCredentialSheet(sheetName)) return '';
  if (isPhoneColumnLabel(header)) return 'phone';
  if (DESCRIPTION_HEADER.test(label) || /^(?:nota|notas|motivo|detalle|descripcion)$/.test(label)) return 'description';
  return '';
}

function isProtectedString(value) {
  const trimmed = value.trim();
  return /^=/.test(trimmed)
    || /^(?:[a-z][a-z\d+.-]*:\/\/|www\.|mailto:|tel:)/i.test(trimmed)
    || /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i.test(trimmed)
    || /^[a-f\d]{24,}$/i.test(trimmed)
    || /^[a-z][a-z\d]*(?:_[a-z\d]+)+$/i.test(trimmed)
    || /^\d{4}-\d{2}-\d{2}(?:$|[T\s])/.test(trimmed)
    || /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}(?:$|\s)/.test(trimmed)
    || /^\d{1,2}\s+\p{L}+\.?\s+\d{4}(?:$|[\s,])/u.test(trimmed);
}

function normalizeWords(value, input, preserveLines = false) {
  const trailingSpace = input && /\s$/.test(value);
  const normalized = value.trim().replace(preserveLines ? /[^\S\r\n]+/g : /\s+/g, ' ');
  const trailingSeparator = trailingSpace ? (preserveLines && /\r?\n$/.test(value) ? '\n' : ' ') : '';
  return { normalized, trailingSeparator };
}

function normalizeString(kind, value, header, options) {
  if (isProtectedString(value)) return value;
  if (kind === 'email') return value.trim().toLocaleLowerCase();
  if (kind === 'phone') {
    if (options.input) return formatPhoneInputForRow(header, value, options.headers, options.row);
    return formatPhoneValueForRow(header, value.trim(), options.headers, options.row);
  }
  const { normalized, trailingSeparator } = normalizeWords(value, options.input, kind === 'description');
  const cased = kind === 'name'
    ? normalized.toLocaleLowerCase().replace(/(^|[^\p{L}\p{M}])(\p{L})/gu, (_, separator, letter) => `${separator}${letter.toLocaleUpperCase()}`)
    : normalized.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase());
  return `${cased}${trailingSeparator}`;
}

function normalizeValue(kind, value, header, options) {
  if (Array.isArray(value)) return value.map((item) => normalizeValue(kind, item, header, options));
  if (typeof value === 'number' && Number.isFinite(value) && kind === 'phone') {
    return normalizeString(kind, String(value), header, options);
  }
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) return value;
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return JSON.stringify(normalizeValue(kind, parsed, header, options));
    } catch {
      // Leave malformed structured values intact rather than changing their syntax.
    }
    return value;
  }
  return normalizeString(kind, value, header, options);
}

function normalizeCellValue(header, value, { sheetName = '', headers = [], row = [], input = false } = {}) {
  if (sheetName && !isNormalizableSheet(sheetName)) return value;
  const kind = getValueKind(header, sheetName);
  return kind ? normalizeValue(kind, value, header, { headers, row, input }) : value;
}

function normalizeRowData(sheetName, headers, row, { input = false } = {}) {
  if (!Array.isArray(row) || !isNormalizableSheet(sheetName)) return row;
  return row.map((value, index) => normalizeCellValue(headers?.[index], value, { sheetName, headers, row, input }));
}

module.exports = { normalizeCellValue, normalizeRowData, isNormalizableSheet };
